import { useEffect, useRef } from 'react'
import SignaturePadLib from 'signature_pad'

import './signature-pad.scss'

const CLASS_NAME = 'signature-pad'
const MAX_SIGNATURE_BASE64_BYTES = 200 * 1024

function exportSignature(pad: SignaturePadLib, canvas: HTMLCanvasElement): string {
  const dataUrl = pad.toDataURL('image/jpeg', 0.5)
  const base64Portion = dataUrl.split(',')[1] || ''

  if (base64Portion.length <= MAX_SIGNATURE_BASE64_BYTES) {
    return dataUrl
  }

  // Resize and re-export if too large
  const scale = Math.sqrt(MAX_SIGNATURE_BASE64_BYTES / base64Portion.length)
  const resizedCanvas = document.createElement('canvas')
  resizedCanvas.width = Math.round(canvas.width * scale)
  resizedCanvas.height = Math.round(canvas.height * scale)
  const ctx = resizedCanvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height)
  }
  return resizedCanvas.toDataURL('image/jpeg', 0.5)
}

export type SignaturePadProps = {
  label?: string
  onSignatureChange: (signature: string | null) => void
}

export const SignaturePad = ({ label, onSignatureChange }: SignaturePadProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const padRef = useRef<SignaturePadLib | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
    })

    pad.addEventListener('endStroke', () => {
      if (pad.isEmpty()) {
        onSignatureChange(null)
      } else {
        onSignatureChange(exportSignature(pad, canvas))
      }
    })

    padRef.current = pad

    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = canvas.offsetWidth * ratio
      canvas.height = canvas.offsetHeight * ratio
      canvas.getContext('2d')?.scale(ratio, ratio)
      pad.clear()
      onSignatureChange(null)
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      pad.off()
    }
  }, [onSignatureChange])

  const handleClear = () => {
    padRef.current?.clear()
    onSignatureChange(null)
  }

  return (
    <section className={CLASS_NAME}>
      {label && <label className={`${CLASS_NAME}__label`}>{label}</label>}
      <section className={`${CLASS_NAME}__canvas-wrapper`}>
        <canvas ref={canvasRef} className={`${CLASS_NAME}__canvas`} />
        <button type="button" className={`${CLASS_NAME}__clear`} onClick={handleClear}>
          Clear
        </button>
      </section>
    </section>
  )
}
